/** @import { ClientOrAdminFirebase, ClientOrAdminDatabase, ClientOrAdminDatabaseReference, ClientOrAdminDatabaseQuery } from '../../_resources/modules/types/ClientOrAdminFirebase.js' */

import { GetAuthToken as getAuthToken } from '../../_resources/modules/GetAuthToken.js';

/**
 * @typedef {Object} jobPermissionsOptions
 * @property {ClientOrAdminFirebase} [firebase] - Firebase app to use. Falls back to globalThis.FirebaseWorker.
 */

/**
 * @typedef {jobPermissionsOptions & { query?: QueryOptions }} JobPermissionsQueryOptions
 */

/**
 * @typedef {Object} AttachListenerOptions
 * @property {boolean} [childEvents] - If true, child events will be used to listen for incremental updates to the data once it has been initially fetched.
 */

/**
 * @typedef {JobPermissionsQueryOptions & AttachListenerOptions} JobPermissionsQueryListenerOptions
 */

/**
 * @typedef {Object} QueryOptions
 * @property {string} [orderByChild] - Child key to order results by.
 * @property {any} [equalTo] - Value to filter results by (requires orderByChild).
 * @property {boolean} [orderByKey] - Order results by key.
 * @property {any} [startAt] - Start value for the query range (inclusive).
 * @property {any} [startAfter] - Start value for the query range (exclusive).
 * @property {any} [endAt] - End value for the query range (inclusive).
 * @property {any} [endBefore] - End value for the query range (exclusive).
 * @property {number} [limitToFirst] - Limit to the first N results.
 * @property {number} [limitToLast] - Limit to the last N results.
 */

/* HELPER METHODS */

/**
 * Get a database instance from the provided firebase app or the global fallback.
 * @param {ClientOrAdminFirebase} [firebase]
 * @returns {ClientOrAdminDatabase}
 */
function getDatabase(firebase) {
  const app = firebase ?? /** @type {ClientOrAdminFirebase} */ (/** @type {any} */ (globalThis).FirebaseWorker);
  return app.database();
}

/**
 * Build a ref at a child path under job_permissions, optionally applying query modifiers.
 * @param {string} childPath - Path relative to job_permissions base (e.g. "companyId/jobs").
 * @param {JobPermissionsQueryOptions} [options]
 * @returns {ClientOrAdminDatabaseQuery}
 */
function getJobPermissionsRef(childPath, options) {
  const basePath = 'photoheight/job_permissions';
  const baseRef = getDatabase(options?.firebase).ref(basePath);
  const ref = baseRef.child(childPath);
  const queryOrRef = options?.query ? applyQuery(ref, options.query) : ref;
  return queryOrRef;
}

/**
 * Apply optional query modifiers to a firebase ref.
 * @param {ClientOrAdminDatabaseQuery} ref
 * @param {QueryOptions} [query]
 * @returns {ClientOrAdminDatabaseQuery}
 */
function applyQuery(ref, query) {
  if (!query) return ref;
  if (query.orderByKey) ref = ref.orderByKey();
  if (query.orderByChild) ref = ref.orderByChild(query.orderByChild);
  if (query.equalTo !== undefined) ref = ref.equalTo(query.equalTo);
  if (query.startAt !== undefined) ref = ref.startAt(query.startAt);
  if (query.startAfter !== undefined) ref = ref.startAfter(query.startAfter);
  if (query.endAt !== undefined) ref = ref.endAt(query.endAt);
  if (query.endBefore !== undefined) ref = ref.endBefore(query.endBefore);
  if (query.limitToFirst !== undefined) ref = ref.limitToFirst(query.limitToFirst);
  if (query.limitToLast !== undefined) ref = ref.limitToLast(query.limitToLast);
  return ref;
}

/**
 * Perform a one-time read on a ref or query.
 * @param {ClientOrAdminDatabaseReference|ClientOrAdminDatabaseQuery} ref
 * @returns {Promise<any>}
 */
async function readOnce(ref) {
  return ref.once('value').then((s) => s.val());
}

/* JOB PERMISSIONS ENTRIES */

/**
 * Fetches /jobs entries for a company with optional querying.
 * @param {string} companyId
 * @param {JobPermissionsQueryOptions} [options]
 * @returns {Promise<Object|null>}
 * @throws {Error} If companyId is not provided.
 */
export async function getJobPermissions(companyId, options) {
  if (!companyId) throw new Error('getJobPermissions: companyId is required');

  const ref = getJobPermissionsRef(`${companyId}/jobs`, options);
  const result = await readOnce(ref);
  return result;
}

/**
 * Fetches /jobs entries for a company where entries have a status of "active".
 * @param {string} companyId
 * @param {JobPermissionsQueryOptions} [options]
 * @returns {Promise<Object|null>}
 * @throws {Error} If companyId is not provided.
 */
export async function getActiveJobPermissions(companyId, options) {
  if (!companyId) throw new Error('getActiveJobPermissions: companyId is required');

  const query = { ...options?.query, orderByChild: 'status', equalTo: 'active' };
  return getJobPermissions(companyId, { ...options, query });
}

/**
 * Fetches an entry from /jobs for a company.
 * @param {string} companyId
 * @param {string} jobId
 * @param {jobPermissionsOptions} [options]
 * @returns {Promise<Object|null>}
 * @throws {Error} If companyId or jobId is not provided.
 */
export async function getJobPermissionsEntry(companyId, jobId, options) {
  if (!companyId) throw new Error('getJobPermissionsEntry: companyId is required');
  if (!jobId) throw new Error('getJobPermissionsEntry: jobId is required');

  const ref = getJobPermissionsRef(`${companyId}/jobs/${jobId}`, options);
  const result = await readOnce(ref);
  return result;
}

/**
 * Fetches a single property from a /jobs entry for a company (e.g. "name", "status").
 * @param {string} companyId
 * @param {string} jobId
 * @param {string} property
 * @param {jobPermissionsOptions} [options]
 * @returns {Promise<any>}
 * @throws {Error} If companyId, jobId, or property is not provided.
 */
export async function getPropertyFromJobPermissionsEntry(companyId, jobId, property, options) {
  if (!companyId) throw new Error('getPropertyFromJobPermissionsEntry: companyId is required');
  if (!jobId) throw new Error('getPropertyFromJobPermissionsEntry: jobId is required');
  if (!property) throw new Error('getPropertyFromJobPermissionsEntry: property is required');

  const ref = getJobPermissionsRef(`${companyId}/jobs/${jobId}/${property}`, options);
  const result = await readOnce(ref);
  return result;
}

/**
 * Find job permissions entries matching a name.
 * @param {string} companyId
 * @param {string} name
 * @param {JobPermissionsQueryOptions} [options]
 * @returns {Promise<Object|null>}
 * @throws {Error} If companyId or name is not provided.
 */
export async function getJobPermissionsEntriesByName(companyId, name, options) {
  if (!companyId) throw new Error('getJobPermissionsEntriesByName: companyId is required');
  if (!name) throw new Error('getJobPermissionsEntriesByName: name is required');

  return getJobPermissions(
    companyId,
    /** @type {JobPermissionsQueryOptions} */ ({ ...options, query: { orderByChild: 'name', equalTo: name } })
  );
}

/* JOB LIST ENTRIES */

/**
 * Fetches /list entries for a company with optional querying.
 * @param {string} companyId
 * @param {JobPermissionsQueryOptions} [options]
 * @returns {Promise<Object|null>}
 * @throws {Error} If companyId is not provided.
 */
export async function getJobList(companyId, options) {
  if (!companyId) throw new Error('getJobList: companyId is required');

  const ref = getJobPermissionsRef(`${companyId}/list`, options);
  const result = await readOnce(ref);
  return result;
}

/**
 * Fetches /list entries for a company where entries have a status of "active".
 * @param {string} companyId
 * @param {JobPermissionsQueryOptions} [options]
 * @returns {Promise<Object|null>}
 * @throws {Error} If companyId is not provided.
 */
export async function getActiveJobList(companyId, options) {
  if (!companyId) throw new Error('getActiveJobList: companyId is required');

  const query = { ...options?.query, orderByChild: 'status', equalTo: 'active' };
  return getJobList(companyId, { ...options, query });
}

/**
 * Fetches an entry from /list for a company.
 * @param {string} companyId
 * @param {string} jobId
 * @param {jobPermissionsOptions} [options]
 * @returns {Promise<Object|null>}
 * @throws {Error} If companyId or jobId is not provided.
 */
export async function getJobListEntry(companyId, jobId, options) {
  if (!companyId) throw new Error('getJobListEntry: companyId is required');
  if (!jobId) throw new Error('getJobListEntry: jobId is required');

  const ref = getJobPermissionsRef(`${companyId}/list/${jobId}`, options);
  const result = await readOnce(ref);
  return result;
}

/**
 * Fetches a single property from a /list entry for a company (e.g. "name").
 * @param {string} companyId
 * @param {string} jobId
 * @param {string} property
 * @param {jobPermissionsOptions} [options]
 * @returns {Promise<any>}
 * @throws {Error} If companyId, jobId, or property is not provided.
 */
export async function getPropertyFromJobListEntry(companyId, jobId, property, options) {
  if (!companyId) throw new Error('getPropertyFromJobListEntry: companyId is required');
  if (!jobId) throw new Error('getPropertyFromJobListEntry: jobId is required');
  if (!property) throw new Error('getPropertyFromJobListEntry: property is required');

  const ref = getJobPermissionsRef(`${companyId}/list/${jobId}/${property}`, options);
  const result = await readOnce(ref);
  return result;
}

/**
 * Find job list entries matching a name.
 * @param {string} companyId
 * @param {string} name
 * @param {JobPermissionsQueryOptions} [options]
 * @returns {Promise<Object|null>}
 * @throws {Error} If companyId or name is not provided.
 */
export async function getJobListEntriesByName(companyId, name, options) {
  if (!companyId) throw new Error('getJobListEntriesByName: companyId is required');
  if (!name) throw new Error('getJobListEntriesByName: name is required');

  const query = { orderByChild: 'name', equalTo: name };
  return getJobList(companyId, /** @type {JobPermissionsQueryOptions} */ ({ ...options, query }));
}

/**
 * Find the first job ID matching a name in the job list.
 * @param {string} companyId
 * @param {string} name
 * @param {jobPermissionsOptions} [options]
 * @returns {Promise<string|null>} The job ID, or null if not found.
 * @throws {Error} If companyId or name is not provided.
 */
export async function getJobIdByName(companyId, name, options) {
  if (!companyId) throw new Error('getJobIdByName: companyId is required');
  if (!name) throw new Error('getJobIdByName: name is required');

  const matches = await getJobListEntriesByName(companyId, name, options);
  return matches ? Object.keys(matches)[0] : null;
}

/**
 * Check whether a job exists in a company's job list.
 * @param {string} companyId
 * @param {string} jobId
 * @param {jobPermissionsOptions} [options]
 * @returns {Promise<boolean>}
 * @throws {Error} If companyId or jobId is not provided.
 */
export async function jobExistsInList(companyId, jobId, options) {
  if (!companyId) throw new Error('jobExistsInList: companyId is required');
  if (!jobId) throw new Error('jobExistsInList: jobId is required');

  const name = await getPropertyFromJobListEntry(companyId, jobId, 'name', options);
  return name != null;
}

/* REAL-TIME LISTENERS */

/**
 * Subscribe to a ref using either a value listener or child event listeners.
 * When options.childEvents is true, performs a one-time read to get the full data,
 * then attaches child_added/child_changed/child_removed listeners to incrementally
 * update the local copy. The callback receives the full data object after each change.
 * Otherwise, subscribes to the value event and calls the callback with the snapshot value.
 * @param {ClientOrAdminDatabaseQuery} ref
 * @param {(data: Object|null) => void} callback
 * @param {AttachListenerOptions} [options]
 * @returns {() => void}
 */
function attachListener(ref, callback, options) {
  if (options?.childEvents) {
    /** @type {Record<string, any> | null} */
    let data = null;
    let gotFirstData = false;
    let disconnected = false;
    /** @type {Record<'child_added' | 'child_changed' | 'child_removed', any>} */
    const handlers = {};

    // Fetch full data first so we know the initial state.
    ref.once('value').then((s) => {
      if (gotFirstData || disconnected) return;
      data = s.val();
      gotFirstData = true;
      callback(data);
    });

    // Attach child listeners — only apply after first full read.
    handlers.child_added = ref.on('child_added', (s) => {
      if (!gotFirstData || s.key == null || disconnected) return;
      if (data == null) data = {};
      if (data[s.key] == null) {
        data[s.key] = s.val();
        callback(data);
      }
    });

    handlers.child_changed = ref.on('child_changed', (s) => {
      if (!gotFirstData || s.key == null || disconnected) return;
      if (data == null) data = {};
      data[s.key] = s.val();
      callback(data);
    });

    handlers.child_removed = ref.on('child_removed', (s) => {
      if (!gotFirstData || s.key == null || disconnected) return;
      if (data != null) {
        delete data[s.key];
        if (Object.keys(data).length === 0) data = null;
        callback(data);
      }
    });

    return () => {
      disconnected = true;
      ref.off('child_added', handlers.child_added);
      ref.off('child_changed', handlers.child_changed);
      ref.off('child_removed', handlers.child_removed);
    };
  }

  const handler = ref.on('value', (snapshot) => callback(snapshot.val()));
  return () => ref.off('value', handler);
}

/**
 * Listen for changes to job permission records.
 * Pass options.childEvents = true to use incremental child listeners instead of a single value listener.
 * @param {string} companyId
 * @param {(data: Object|null) => void} callback
 * @param {JobPermissionsQueryListenerOptions} [options]
 * @returns {() => void} - A method for disconnecting the listener
 * @throws {Error} If companyId is not provided or callback is not a function.
 */
export function onJobPermissionsChanged(companyId, callback, options) {
  if (!companyId) throw new Error('onJobPermissionsChanged: companyId is required');
  if (typeof callback !== 'function') throw new Error('onJobPermissionsChanged: callback must be a function');

  const ref = getJobPermissionsRef(`${companyId}/jobs`, options);
  return attachListener(ref, callback, options);
}

/**
 * Listen for changes to active job permission records (status === "active").
 * Pass options.childEvents = true to use incremental child listeners instead of a single value listener.
 * @param {string} companyId
 * @param {(data: Object|null) => void} callback
 * @param {JobPermissionsQueryListenerOptions} [options]
 * @returns {() => void} - A method for disconnecting the listener
 * @throws {Error} If companyId is not provided or callback is not a function.
 */
export function onActiveJobPermissionsChanged(companyId, callback, options) {
  if (!companyId) throw new Error('onActiveJobPermissionsChanged: companyId is required');
  if (typeof callback !== 'function') throw new Error('onActiveJobPermissionsChanged: callback must be a function');

  const query = { ...options?.query, orderByChild: 'status', equalTo: 'active' };
  return onJobPermissionsChanged(companyId, callback, { ...options, query });
}

/**
 * Listen for changes to job list entries.
 * Pass options.childEvents = true to use incremental child listeners instead of a single value listener.
 * @param {string} companyId
 * @param {(data: Object|null) => void} callback
 * @param {JobPermissionsQueryListenerOptions} [options]
 * @returns {() => void} - A method for disconnecting the listener
 * @throws {Error} If companyId is not provided or callback is not a function.
 */
export function onJobListChanged(companyId, callback, options) {
  if (!companyId) throw new Error('onJobListChanged: companyId is required');
  if (typeof callback !== 'function') throw new Error('onJobListChanged: callback must be a function');

  const ref = getJobPermissionsRef(`${companyId}/list`, options);
  return attachListener(ref, callback, options);
}

/**
 * Listen for changes to active job list entries (status === "active").
 * Pass options.childEvents = true to use incremental child listeners instead of a single value listener.
 * @param {string} companyId
 * @param {(data: Object|null) => void} callback
 * @param {JobPermissionsQueryListenerOptions} [options]
 * @returns {() => void} - A method for disconnecting the listener
 * @throws {Error} If companyId is not provided or callback is not a function.
 */
export function onActiveJobListChanged(companyId, callback, options) {
  if (!companyId) throw new Error('onActiveJobListChanged: companyId is required');
  if (typeof callback !== 'function') throw new Error('onActiveJobListChanged: callback must be a function');

  const query = { ...options?.query, orderByChild: 'status', equalTo: 'active' };
  return onJobListChanged(companyId, callback, { ...options, query });
}

/**
 * Listen for changes to a single job list entry.
 * @param {string} companyId
 * @param {string} jobId
 * @param {(data: Object|null) => void} callback
 * @param {jobPermissionsOptions} [options]
 * @returns {() => void} - A method for disconnecting the listener
 * @throws {Error} If companyId or jobId is not provided or callback is not a function.
 */
export function onJobListEntryChanged(companyId, jobId, callback, options) {
  if (!companyId) throw new Error('onJobListEntryChanged: companyId is required');
  if (!jobId) throw new Error('onJobListEntryChanged: jobId is required');
  if (typeof callback !== 'function') throw new Error('onJobListEntryChanged: callback must be a function');

  const ref = getJobPermissionsRef(`${companyId}/list/${jobId}`, options);
  return attachListener(ref, callback);
}

/* SHALLOW KEY READS */

/**
 * Perform a shallow REST read on a ref, returning only the keys at that location.
 * @param {ClientOrAdminDatabaseReference | ClientOrAdminDatabaseQuery} ref
 * @returns {Promise<string[]>} Array of keys, or empty array if no data.
 */
async function shallowReadKeys(ref) {
  const url = ref.toString();
  const tokenResult = await getAuthToken();
  const authParam = tokenResult?.type === 'id_token' ? 'auth' : 'access_token';
  const response = await fetch(`${url}.json?shallow=true&${authParam}=${tokenResult?.token}`);
  const result = await response.json();
  if (result?.error === 'Permission denied') {
    throw new Error(`shallowReadKeys: Permission denied at ${url}`);
  }
  return result ? Object.keys(result) : [];
}

/**
 * Fetches only the keys under /list for a company using a shallow REST read.
 * @param {string} companyId
 * @param {JobPermissionsQueryOptions} [options]
 * @returns {Promise<string[]>} Array of job IDs (keys), or empty array if none.
 * @throws {Error} If companyId is not provided.
 */
export async function getJobListKeys(companyId, options) {
  if (!companyId) throw new Error('getJobListKeys: companyId is required');

  const ref = getJobPermissionsRef(`${companyId}/list`, options);
  const result = await shallowReadKeys(ref);
  return result;
}

/**
 * Fetches only the keys under /jobs for a company using a shallow REST read.
 * @param {string} companyId
 * @param {JobPermissionsQueryOptions} [options]
 * @returns {Promise<string[]>} Array of job IDs (keys), or empty array if none.
 * @throws {Error} If companyId is not provided.
 */
export async function getJobPermissionsKeys(companyId, options) {
  if (!companyId) throw new Error('getJobPermissionsKeys: companyId is required');

  const ref = getJobPermissionsRef(`${companyId}/jobs`, options);
  const result = await shallowReadKeys(ref);
  return result;
}
